/**
 * Result export helpers
 * =====================
 * - PDF  : compact grayish A4 sheet rendered via a print window
 * - Excel: real .xlsx built with SheetJS
 *
 * `rows` are already-filtered student rows from the table; `subjects`
 * is the ordered subject list; `grades` are the configured bands.
 */

import * as XLSX from 'xlsx';
import { gradeForPercentage } from './resultsApi';

const escapeHtml = (v) =>
  String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const buildHeaderCells = (subjects) =>
  subjects.map((s) => `<th>${escapeHtml(s)}</th>`).join('');

const buildRow = (s, idx, subjects, grades) => {
  const subjectCells = subjects
    .map((subj) => {
      const m = s.marks[subj];
      return `<td>${m ? `${escapeHtml(m.obtained)}<span class="f">/${escapeHtml(m.full)}</span>` : '—'}</td>`;
    })
    .join('');
  const grade = gradeForPercentage(s.percentage, grades);
  return `<tr>
    <td>${idx + 1}</td>
    <td class="l">${escapeHtml(s.Student_Name || '—')}${s.Admission_Number ? `<span class="sub">Adm: ${escapeHtml(s.Admission_Number)}</span>` : ''}</td>
    <td>${escapeHtml(s.Class || '—')}</td>
    <td>${escapeHtml(s.Section || '—')}</td>
    ${subjectCells}
    <td class="b">${escapeHtml(s.obtained)}<span class="f">/${escapeHtml(s.fullMarks)}</span></td>
    <td>${escapeHtml(s.percentage)}%</td>
    <td class="b">${escapeHtml(grade)}</td>
  </tr>`;
};

export const exportResultsPDF = (exam, subjects, rows, grades = []) => {
  const title = `${exam?.Exam_Name || 'Exam'} — Results`;
  const body = rows.map((s, i) => buildRow(s, i, subjects, grades)).join('');

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body {
    font-family: 'Segoe UI', Arial, sans-serif;
    color: #2b2b2b;
    margin: 0;
    font-size: 8.5px;
  }
  .head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 1.5px solid #555; padding-bottom: 6px; margin-bottom: 8px; }
  .head h1 { font-size: 13px; margin: 0; color: #333; }
  .head .meta { font-size: 8px; color: #777; text-align: right; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 0.5px solid #bbb; padding: 3px 4px; text-align: center; }
  thead th { background: #e9e9ec; color: #333; font-weight: 600; }
  tbody tr:nth-child(even) td { background: #f6f6f7; }
  td.l { text-align: left; }
  td.b { font-weight: 700; }
  .f { color: #999; font-size: 7px; }
  .sub { display: block; color: #888; font-size: 7px; }
  tfoot td { border: none; padding-top: 8px; color: #888; font-size: 7.5px; }
</style>
</head>
<body>
  <div class="head">
    <h1>${escapeHtml(exam?.Exam_Name || 'Exam Results')}</h1>
    <div class="meta">
      ${rows.length} student${rows.length !== 1 ? 's' : ''} · ${subjects.length} subject${subjects.length !== 1 ? 's' : ''}<br/>
      Generated ${new Date().toLocaleString()}
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th><th>Student</th><th>Class</th><th>Sec</th>
        ${buildHeaderCells(subjects)}
        <th>Total</th><th>%</th><th>Grade</th>
      </tr>
    </thead>
    <tbody>${body}</tbody>
  </table>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 400);
};

export const exportResultsExcel = (exam, subjects, rows, grades = []) => {
  const header = ['#', 'Student', 'Admission No', 'Class', 'Section', ...subjects, 'Total', 'Full', '%', 'Grade'];

  const data = rows.map((s, idx) => {
    const subjectVals = subjects.map((subj) => {
      const m = s.marks[subj];
      return m ? m.obtained : '';
    });
    return [
      idx + 1,
      s.Student_Name || '',
      s.Admission_Number || '',
      s.Class || '',
      s.Section || '',
      ...subjectVals,
      s.obtained,
      s.fullMarks,
      s.percentage,
      gradeForPercentage(s.percentage, grades),
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
  ws['!cols'] = header.map((h, i) => ({ wch: i === 1 ? 24 : Math.max(6, String(h).length + 2) }));

  const wb = XLSX.utils.book_new();
  const sheetName = (exam?.Exam_Short_Name || exam?.Exam_Name || 'Results').slice(0, 28);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const fileName = `${(exam?.Exam_Name || 'results').replace(/[^\w-]+/g, '_')}.xlsx`;
  XLSX.writeFile(wb, fileName);
};
